import type { TFunction } from "i18next";
import { AtLg } from "@/components/breakpoint";
import {
  allDropped,
  reelPrizes,
  topDroppingPrize,
  type PrizeDrop,
} from "./types";
import { PrizeDropContent } from "./launcher";
import { formatButtonPrizeTitle } from "@/utils/helpers";
import { timeLeftRing } from "@/igw-button/ring";

import { optInRows, registerCampaignType } from "../../registry";
import { canJoinCampaign } from "../../can-join";
import { campaignKey } from "../../campaign-key";
import { campaignDisplayTitle } from "@/utils/campaign-helpers";
import type { CampaignTypeModule, CurrentCampaign } from "../../registry";
import type { AppStore } from "@/stores/app.store";
import { selectPrizeDropById } from "@/stores/prizedrop.store";
import { selectJoined } from "@/stores/campaigns.store";
import {
  isActiveCampaignStatus,
  isFinishedCampaignStatus,
} from "../../campaign-status";

import type { CampaignView } from "@/components/campaigns/campaign-shell/campaign-view";
import { FINISHED_DIM } from "@/components/campaigns/campaign-shell/campaign-view";
import {
  CampaignDescriptionText,
  EligibleGamesSection,
  HowToWinCard,
  InfoSection,
  CampaignTimeLeftChip,
} from "@/components/campaigns/campaign-shell/campaign-sections";
import {
  PRIZEDROP_HOW_TO_WIN,
  PrizeDropList,
  RemainingPrizeDrops,
} from "@/components/campaigns/prizedrops/prizedrop-sections";
import {
  CampaignJoinCta,
  CampaignJoinStickyBar,
} from "@/components/campaign-card/campaign-join-cta";
import { CampaignFinishedCard } from "@/components/campaign-card/campaign-finished-card";
import { PrizeDropDockPanel } from "@/components/campaigns/prizedrops/prizedrop-dock-panel";
import { PrizeDropHomeCard } from "@/components/campaigns/prizedrops/prizedrop-home-card";
import { PrizeDropRevealCard } from "@/components/campaigns/prizedrops/prizedrop-reveal-card";
import { FinishedPrizeDropCard } from "@/components/campaign-card/finished-campaign/finished-prizedrop-card";
import { cn } from "@/lib/utils";

const prizeDropData = (current: CurrentCampaign): PrizeDrop =>
  current.data as PrizeDrop;

function detailView(
  current: CurrentCampaign,
  t: TFunction
): CampaignView | null {
  if (current.type !== "prizedrop") return null;
  const pd = prizeDropData(current);
  const finished = pd.campaign.status === "FI";
  const joined = current.joined;
  // Only campaigns that require opt-in show a Join CTA ("NONE" = no opt-in),
  // and none once every prize has dropped — the button's "All dropped", one
  // answer on every surface (INT-06).
  const canJoin = canJoinCampaign(pd.campaign, joined) && !allDropped(pd);
  const finishedTitle = t(
    "campaign.prizeDropsFinished",
    "Prize drops finished"
  );
  const howToWin = (
    <HowToWinCard
      text={t("prizedrop.howToWin", PRIZEDROP_HOW_TO_WIN)}
      minimumBet={pd.minimum_bet}
    />
  );
  return {
    campaign: pd.campaign,
    primaryLabel: t("prizedrop.tabLabel", "Prize Drops"),
    finished,
    // The reveal card leads both surfaces (desktop: top of the sidebar, right
    // under the campaign banner; mobile: under the tabs via mobileNotice, so a
    // win isn't buried below the prize list). It renders itself away when there
    // is no unrevealed win, and is deliberately NEVER inside FINISHED_DIM — a
    // win on a finished campaign still has to be revealable (and acked).
    mobileNotice: (
      <>
        {/* The reveal window has one owner per layout: this mount on phones (where the
            sidebar's card is folded in too, hidden), the sidebar's on desktop (where this
            notice is not rendered at all) — the reveal's open state lives in the store, and a
            portalled window escapes `hidden lg:flex`, so two owners would render it twice. */}
        <PrizeDropRevealCard prizeDrop={pd} ownsWindowOn="mobile" />
        {finished && (
          <CampaignFinishedCard campaign={pd.campaign} title={finishedTitle} />
        )}
      </>
    ),
    sidebar: finished ? (
      // Finished: the finished card takes the CTA slot on desktop (mobile
      // shows it under the tabs via mobileNotice); how-to-win stays but
      // greyed out (Figma 5086:1740).
      <>
        <PrizeDropRevealCard
          prizeDrop={pd}
          ownsWindowOn="desktop"
          className="hidden lg:flex"
        />
        <CampaignFinishedCard
          campaign={pd.campaign}
          title={finishedTitle}
          className="hidden lg:flex"
        />
        {/* Both stat blocks are history once the campaign is over, so they grey out together
            — the reveal and finished cards above stay bright, since a win on a finished
            campaign is still revealable. Mirrors the Sprint Mission sidebar. */}
        <AtLg>
          <div
            className={cn(FINISHED_DIM, "flex w-full flex-col gap-2 lg:gap-4")}
          >
            {howToWin}
            <RemainingPrizeDrops prizeDrop={pd} />
          </div>
        </AtLg>
      </>
    ) : (
      <>
        <PrizeDropRevealCard
          prizeDrop={pd}
          ownsWindowOn="desktop"
          className="hidden lg:flex"
        />
        {/* Not joined: the desktop join CTA sits at the top of the sidebar
            (mobile uses the sticky bar — see mobileStickyAction). Mirrors the
            tournament view; the opt-in action + components are type-agnostic. */}
        {canJoin && (
          <CampaignJoinCta campaign={pd.campaign} className="hidden lg:flex" />
        )}
        <AtLg>
          {howToWin}
          <RemainingPrizeDrops prizeDrop={pd} />
        </AtLg>
      </>
    ),
    guide: finished ? <div className={FINISHED_DIM}>{howToWin}</div> : howToWin,
    // Phones show the tiers alone: the list already says what is left, so the big remaining
    // count stays a desktop sidebar card.
    primary: (
      <div
        className={cn(
          "flex w-full flex-col gap-2 lg:gap-3",
          finished && FINISHED_DIM
        )}
      >
        <PrizeDropList prizeDrop={pd} />
      </div>
    ),
    // Sideways: what is left as the hero on the left (Opt In pinned under it), the tiers on the
    // right.
    landscape: {
      narrowSide: "start",
      narrow: (
        <RemainingPrizeDrops
          prizeDrop={pd}
          className={cn(finished && FINISHED_DIM)}
        />
      ),
      wide: (
        <div
          className={cn("flex w-full flex-col gap-2", finished && FINISHED_DIM)}
        >
          <PrizeDropList prizeDrop={pd} />
        </div>
      ),
    },
    info: (
      <div className="flex w-full flex-col gap-2 lg:gap-4">
        <InfoSection
          title={t("campaign.aboutHeading", "About")}
          aside={<CampaignTimeLeftChip campaign={pd.campaign} />}
        >
          <CampaignDescriptionText text={pd.campaign.campaign_info} />
        </InfoSection>
        <EligibleGamesSection
          games={pd.eligible_games}
          count={pd.eligible_games_count}
          campaignId={pd.campaign.id}
        />
      </div>
    ),
    canJoin,
    mobileStickyAction: canJoin ? (
      <CampaignJoinStickyBar campaign={pd.campaign} />
    ) : undefined,
  };
}

/** Dynamic Prize Drops (`campaign.type === "DP"`). */
const prizeDropModule: CampaignTypeModule = {
  id: "prizedrop",
  providers: true,
  resolve(state: AppStore, key): CurrentCampaign | null {
    const pd = selectPrizeDropById(state, key);
    if (!pd) return null;
    return {
      type: "prizedrop",
      campaign: pd.campaign,
      joined: selectJoined(state, key),
      data: pd,
    };
  },
  listActive: (state) =>
    state.prizeDrops
      .filter((pd) => isActiveCampaignStatus(pd.campaign.status))
      .map((pd) => ({ campaign: pd.campaign, data: pd })),
  listFinished: (state) =>
    state.prizeDrops
      .filter((pd) => isFinishedCampaignStatus(pd.campaign.status))
      .map((pd) => ({ campaign: pd.campaign, data: pd })),
  launcher: {
    // Its own content: the counter over the rolling drops (`./launcher`).
    Content: PrizeDropContent,
    // Its pieces move themselves, like metal (Sebastian, 2026-10-21: "no
    // scaling or wobbly"): the shell's pop and slurp are off.
    contentMoves: true,
    // The coin's ring: the time left, as a tournament's (the drops stop at
    // its end — a player's "how long can I still win one?").
    ring: (campaign, now) => timeLeftRing(campaign, now),
    // Whether you opted in arrives with the progress stream: until then the
    // launcher holds the coin pending (a campaign without opt-in needn't
    // wait) — never live drops to someone who can't win one (missions' rule).
    pending: (current, state) =>
      current.campaign.opt_in_method !== "NONE" &&
      !(campaignKey(current.campaign) in state.joinedById),
    // An ended drop keeps its place for its display window, finished.
    showsFinished: true,
    // The states around the drops are the shared set's, as the other types'
    // (Sebastian, 2026-10-22: "1, 2, 3" — the missing prize-drop states);
    // otherwise the content draws the drops and the rows only name the
    // control.
    rows: (current, state, t) => {
      const pd = prizeDropData(current);
      // Finished: "Finished" alone on the dark chip — a drop has no standing
      // to show; a win of yours is revealed as it lands (the reveal set
      // outranks these rows).
      if (isFinishedCampaignStatus(current.campaign.status))
        return { hero: t("igwButton.finished"), heroDisabled: true };
      // Everything dropped: "All dropped" on the dark chip, no gold
      // (IGB-PD-09) — before the ask: nothing left to join for.
      if (allDropped(pd))
        return { hero: t("prizedrop.allDropped"), heroFaded: true };
      // Not opted in: the ask is the hero, the top prize its banner — the
      // tournament's and missions' set (IGB-PD-07), in the panel's words.
      if (
        canJoinCampaign(current.campaign, current.joined) &&
        campaignKey(current.campaign) in state.joinedById
      ) {
        return optInRows(current.campaign, t);
      }
      return { hero: campaignDisplayTitle(current.campaign, t) };
    },
    // Your win takes over the button (IGB-PD-06, as built: the old reveal
    // button's pieces on the rows): "Reveal Prize" is the CTA hero, "You
    // have won!" the banner riding the top, the count of waiting prizes on
    // its end above one. The prize itself stays hidden until the reveal.
    revealReel: (state, key) => {
      const pd = selectPrizeDropById(state, key);
      return pd ? reelPrizes(pd) : [];
    },
    reveal: (pending, t) => ({
      hero: t("igwButton.revealPrize"),
      heroKind: "action",
      top: t("igwButton.youHaveWon"),
      topKind: "banner",
      topCount: pending > 1 ? pending : undefined,
    }),
  },
  hook: (entry, t) => {
    const top = topDroppingPrize(entry.data as PrizeDrop);
    return top
      ? {
          label: t("campaign.topPrize", "Top prize"),
          value: formatButtonPrizeTitle(top.label, t),
          isCatalogItem: top.is_catalog_item,
        }
      : null;
  },
  homeCard: (entry, layout) => (
    <PrizeDropHomeCard prizeDrop={entry.data as PrizeDrop} layout={layout} />
  ),
  finishedCard: (entry) => (
    <FinishedPrizeDropCard prizeDrop={entry.data as PrizeDrop} />
  ),
  detailView,
  dockPanel: (current) =>
    current.type === "prizedrop" ? (
      <PrizeDropDockPanel
        prizeDrop={prizeDropData(current)}
        joined={current.joined}
      />
    ) : null,
};

registerCampaignType(prizeDropModule);
